/********************************************************************************
 * Copyright (c) 2022 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
const HeaderSection = require('./HeaderSection');
const JSG = require('../../JSG');

class HeaderModel {
	constructor() {
		this._size = 100;
		this._initialSection = 0;
		this._sections = [];
		this._defaultSectionSize = 2000;
		this._outlineDirection = 'above';
	}

	get sections() {
		return this._sections;
	}

	get size() {
		return this._size;
	}

	set size(size) {
		this._size = size;
	}

	get initialSection() {
		return this._initialSection;
	}

	get defaultSectionSize() {
		return this._defaultSectionSize;
	}

	set defaultSectionSize(size) {
		this._defaultSectionSize = size;
	}

	get outlineDirection() {
		return this._outlineDirection;
	}

	set outlineDirection(direction) {
		this._outlineDirection = direction;
	}

	getSectionDefaults(index) {
		return { size: this.defaultSectionSize, level: 0, visible: true };
	}

	getSectionsSize(range) {
		let i;
		let pos = -0;

		for (i = 0; i < this.size; i += 1) {
			pos += this.getSectionSize(i, range);
		}

		return pos;
	}

	getSectionSize(index, range) {
		const data = this._sections[index];

		if (range && !this.isInRange(index, range)) {
			return 0;
		}

		if (data) {
			if (data._parent !== undefined) {
				let parent = this._sections[data._parent];
				while (parent) {
					if (parent && parent._closed) {
						return 0;
					}
					parent = parent._parent === undefined ? undefined : this._sections[parent._parent];
				}
			}
			return data._visible ? data._size : 0;
		}

		return this.defaultSectionSize;
	}

	setSectionClosed(index, closed) {
		if (closed === undefined && this._sections[index] === undefined) {
			return;
		}

		const section = this.getOrCreateSectionAt(index);
		if (section !== undefined) {
			section._closed = closed ? true : undefined;
			if (!section._closed) {
				const max = this.size;
				let parent;

				if (this.outlineDirection === 'above') {
					for (let i = index + 1; i < max; i += 1) {
						parent = this.getSectionParent(i);
						if (parent === index) {
							this.setSectionVisible(i, true);
						}
					}
				} else {
					for (let i = index - 1; i >= 0; i -= 1) {
						parent = this.getSectionParent(i);
						if (parent === index) {
							this.setSectionVisible(i, true);
						}
					}
				}
			}
		}
	}

	getSectionClosed(index) {
		const data = this._sections[index];

		if (data) {
			return data._closed === true;
		}
		return false;
	}

	setSectionSize(index, size) {
		const section = this.getOrCreateSectionAt(index);
		if (section !== undefined) {
			section._size = size < 0 ? 0 : size;
			section._visible = size > 0;
			if (section._parent) {
				const data = this._sections[section._parent];
				if (data && data._closed) {
					data._closed = undefined;
				}
			}
		}
	}

	getSectionLevel(index) {
		const data = this._sections[index];

		return data ? data.level : 0;
	}

	setSectionLevel(index, level) {
		const section = this.getOrCreateSectionAt(index);
		if (section !== undefined) {
			section.level = Math.max(0, level);
		}
	}

	getSectionParent(index) {
		const data = this._sections[index];

		return data ? data.parent : undefined;
	}

	setSectionParent(index, parent) {
		const section = this.getOrCreateSectionAt(index);
		if (section !== undefined) {
			section.parent = parent;
		}
	}

	setSectionVisible(index, visible) {
		const section = this.getOrCreateSectionAt(index);
		if (section !== undefined) {
			section.setVisible(visible);
			if (visible && section.getSize() === 0) {
				section.setSize(this.defaultSectionSize);
			}
			if (section._parent !== undefined) {
				const data = this._sections[section._parent];
				if (data && data._closed) {
					data._closed = undefined;
				}
			}
		}
	}

	getSectionTitle(index) {
		return undefined;
	}

	getSectionFormat(index) {
		const data = this._sections[index];
		if (data && data._properties) {
			return data._properties.formats;
		}
		return undefined;
	}

	getSectionTextFormat(index) {
		const data = this._sections[index];
		if (data && data._properties) {
			return data._properties.textFormats;
		}
		return undefined;
	}

	getSectionAttributes(index) {
		const data = this._sections[index];
		if (data && data._properties) {
			return data._properties.cellAttributes;
		}
		return undefined;
	}

	getSectionAt(index) {
		if (index >= 0) {
			return this._sections[index];
		}
		return undefined;
	}

	setSectionAt(index, data) {
		this._sections[index] = data;
	}

	getOrCreateSectionAt(index) {
		let section = this.getSectionAt(index);
		if (section === undefined) {
			section = new HeaderSection();
			section._size = this.defaultSectionSize;
			this._sections[index] = section;
		}

		return section;
	}

	getSection(pos, range) {
		let current;
		let index = 0;

		current = 0;
		while (pos >= current && index <= this.size) {
			current += this.getSectionSize(index, range);
			index += 1;
		}

		return Math.max(0, index - 1);
	}

	getSectionAndOffset(pos, range) {
		let current;
		let index = 0;
		let offset = 0;

		current = 0;
		while (pos >= current && index <= this.size) {
			offset = pos - current;
			current += this.getSectionSize(index, range);
			index += 1;
		}

		return {
			index: Math.max(0, index - 1),
			offset: Math.round(offset),
		};
	}

	getSectionPos(index, range) {
		let pos = 0;
		let i;
		let size;

		for (i = 0; i < index; i += 1) {
			size = this.getSectionSize(i, range);
			pos += size;
		}

		return pos;
	}

	getSectionSplit(pos, range) {
		const tolerance = JSG.findRadius / 2;
		const section = this.getSection(pos);
		let sectionPos = this.getSectionPos(section);

		if (Math.abs(pos - sectionPos) < tolerance) {
			if (section > 0) {
				return section - 1;
			}
		}

		sectionPos += this.getSectionSize(section, range);

		if (Math.abs(sectionPos - pos) < tolerance) {
			return section;
		}

		return undefined;
	}

	iterateSections(callback) {
		const defSection = new HeaderSection();
		let section;
		let i;
		let ret;
		const n = this.size;
		const sectionData = this._sections;

		defSection.setSize(this.defaultSectionSize);

		for (i = 0; i < n; i += 1) {
			section = sectionData[i];
			if (section) {
				ret = callback(section, i);
			} else {
				ret = callback(defSection, i);
			}
			if (ret === false) {
				break;
			}
		}
	}

	clear() {
		this._sections.length = 0;
	}
}

class RowHeaderModel extends HeaderModel {
	constructor() {
		super();

		this._defaultSectionSize = 500;

	}

	isInRange(index, range) {
		return (index >= range.getY1() && index <= range.getY2());
	}

	getSectionFromReference(ref) {
		return this.getOrCreateSectionAt(Number(ref) - 1);
	}
}

const IF_SECTION_DEFS = { size: 700, level: 0, visible: true };
const COMMENT_SECTION_DEFS = { size: 0, level: 0, visible: false };

class ColumnHeaderModel extends HeaderModel {
	constructor() {
		super();

		this.mincol = -2;
		this.maxcol = 50;
		this._initialSection = -2;
		this.setSectionSize(0, 0);
		this.setSectionSize(1, 700);
	}

	get size() {
		return this.maxcol - this.mincol;
	}

	set size(size) {
		this.maxcol = size - this.mincol;
	}

	isInRange(index, range) {
		return index >= range.getX1() && index <= range.getX2();
	}

	getSectionDefaults(index) {
		if (index === 0) return { ...COMMENT_SECTION_DEFS };
		return index === 1 ? { ...IF_SECTION_DEFS } : super.getSectionDefaults(index);
	}

	getSectionSize(index, range) {
		let size = super.getSectionSize(index, range);

		if (this.showFormulas) {
			size *= 1.5;
		}

		return size;
	}

	setSectionSize(index, size) {
		if (this.showFormulas) {
			size /= 1.5;
		}

		super.setSectionSize(index, size);
	}

	getSectionTitle(index) {
		switch (index) {
			case 0:
				return 'COMMENT';
			case 1:
				return 'IF';
		}
		return undefined;
	}

	getSectionFromReference(ref) {
		let colVal = 0;

		switch (ref) {
			case 'COMMENT':
				break;
			case 'IF':
				colVal = 1;
				break;
			default:
				for (let j = 0; j < ref.length; j += 1) {
					colVal = 26 * colVal + ref.charAt(j).toUpperCase().charCodeAt(0) - 65 + 1; // 65 -> 'A'
				}
				colVal += 1;
				break;
		}

		return this.getOrCreateSectionAt(colVal);
	}
}

module.exports = {
	HeaderModel,
	RowHeaderModel,
	ColumnHeaderModel
};
